import { el, awaitRedraw } from "@dhx/ts-common/dom";
import { isDefined, isEmptyObj } from "@dhx/ts-common/core";
import {
	getFormItemCss,
	validateInput,
	getValidationMessage,
	isVerify,
	baseInputValidate,
	baseProps,
} from "../helper";
import { Label } from "./helper/label";
import { IEventSystem, EventSystem } from "@dhx/ts-common/events";
import {
	IInputConfig,
	ValidationStatus,
	IInputEventHandlersMap,
	ItemEvent,
	IInput,
	IInputProps,
	ITextConfig,
	ITextAreaConfig,
} from "../types";

export class Input extends Label implements IInput {
	public config: IInputConfig;
	public events: IEventSystem<ItemEvent, IInputEventHandlersMap> = new EventSystem();

	protected _propsItem = [
		"inputType",
		"required",
		"validation",
		"icon",
		"placeholder",
		"autocomplete",
		"readOnly",
		"maxlength",
		"minlength",
		"min",
		"max",
		"step",
		"label",
		"labelWidth",
		"labelPosition",
		"hiddenLabel",
		"helpMessage",
		"preMessage",
		"successMessage",
		"errorMessage",
	];
	protected _props = [...baseProps, ...this._propsItem];
	private _isValid = true;

	constructor(container: HTMLElement | string, config = {}) {
		super(null, config);

		this._initView(config);
		this._initHandlers();
	}
	destructor() {
		this.events && this.events.clear();
		this.events = this._uid = this._isValid = this._propsItem = this._propsItem = null;

		super._destructor();
		this.unmount();
	}
	setProperties(propertyConfig: IInputProps): void {
		if (
			!propertyConfig ||
			isEmptyObj(propertyConfig) ||
			!this.events.fire(ItemEvent.beforeChangeProperties, [this.getProperties()])
		)
			return;

		for (const key in propertyConfig) {
			if (this._props.includes(key)) {
				this.config[key] = propertyConfig[key];
			}
		}
		this.events.fire(ItemEvent.afterChangeProperties, [this.getProperties()]);
		this.paint();
	}
	getProperties(): IInputProps {
		const obj: IInputProps = {};
		for (const prop of this._props) {
			obj[prop] = this.config[prop];
		}
		return obj;
	}
	show(): void {
		const { value, hidden } = this.config;
		if (!hidden || !this.events.fire(ItemEvent.beforeShow, [value])) {
			return;
		}
		this.config.hidden = false;
		this.events.fire(ItemEvent.afterShow, [value]);
	}
	hide(init?: boolean): void {
		const { value, hidden } = this.config;
		if ((hidden && !init) || !this.events.fire(ItemEvent.beforeHide, [value, init])) {
			return;
		}
		this.config.hidden = true;
		this.events.fire(ItemEvent.afterHide, [value, init]);
	}
	isVisible(): boolean {
		return !this.config.hidden;
	}
	disable(): void {
		this.config.disabled = true;
		this.paint();
	}
	enable(): void {
		this.config.disabled = false;
		this.paint();
	}
	isDisabled(): boolean {
		return this.config.disabled;
	}
	validate(silent = false, validateValue?: string | number): boolean {
		const value = typeof validateValue === "undefined" ? this.getValue() : validateValue;

		if (!silent && !this.events.fire(ItemEvent.beforeValidate, [value])) return;
		this._isValid = this.config.validation
			? validateInput(value, this.config.validation)
			: this.config.required
			? !!(baseInputValidate(value, this.config) && String(value).trim().length)
			: baseInputValidate(value, this.config);

		if (!silent) {
			this.config.$validationStatus = this._isValid ? ValidationStatus.success : ValidationStatus.error;
			this.events.fire(ItemEvent.afterValidate, [value, this._isValid]);
		}

		return this._isValid;
	}
	clearValidate(): void {
		this.config.$validationStatus = ValidationStatus.pre;
		this.paint();
	}
	clear(): void {
		if (this.config.value === "") return;

		this.config.value = "";
		this.events.fire(ItemEvent.change, [this.getValue()]);
	}
	setValue(value: string | number): void {
		if (typeof value === "undefined" || this.config.value === value) return;

		this.config.value = value;
		this.events.fire(ItemEvent.change, [this.getValue()]);
		isVerify(this.config) && this.validate();
	}
	getValue(): string | number {
		const { inputType, value } = this.config;
		if ((inputType === "number" && typeof value === "number") || value === null) {
			return value;
		}
		if (inputType === "number" && typeof value === "string") {
			return value.length ? Number(value) : "";
		}
		if (typeof value === "string") {
			return value.length ? value : "";
		}
		return typeof value === "undefined" ? "" : String(value);
	}
	focus(): void {
		awaitRedraw().then(() => {
			this.getRootView().refs.input.el.focus();
		});
	}
	blur(): void {
		awaitRedraw().then(() => {
			this.getRootView().refs.input.el.blur();
		});
	}
	protected _initView(config: IInputConfig | ITextConfig | ITextAreaConfig): void {
		if (isEmptyObj(config)) {
			throw new Error("Check the configuration is correct");
		}
		this.config = {
			type: "input",
			id: config.id,
			name: config.name,
			disabled: false,
			hidden: false,
			inputType: "text",
			required: false,
			icon: "",
			placeholder: "",
			autocomplete: false,
			readOnly: false,
			label: "",
			labelWidth: "",
			labelPosition: "top",
			hiddenLabel: false,
			helpMessage: "",
			preMessage: "",
			successMessage: "",
			errorMessage: "",
			width: "content",
			height: "content",
		};
		for (const key in config) {
			if (key !== "id" && key !== "type" && key !== "name") {
				this.config[key] = config[key];
			}
		}
		if (this.config.hidden) {
			awaitRedraw().then(() => {
				this.hide(true);
			});
		}
		this.paint();
	}
	protected _initHandlers() {
		this.events.on(ItemEvent.change, () => this.paint());
		this.events.on(ItemEvent.afterValidate, () => {
			this.config.$validationStatus = this._isValid ? ValidationStatus.success : ValidationStatus.error;
			this.paint();
		});
	}
	protected _getHandlers() {
		return {
			oninput: (e: Event) => {
				const value = (e.target as HTMLInputElement).value;
				this.config.value = value;
				this.events.fire(ItemEvent.input, [value]);
			},
			onchange: (e: Event) => {
				const value = (e.target as HTMLInputElement).value;
				this.config.value = value;
				this.events.fire(ItemEvent.change, [this.getValue()]);
				isVerify(this.config) && this.validate();
				this.paint();
			},
			onfocus: () => {
				this.events.fire(ItemEvent.focus, [this.getValue()]);
				this.paint();
			},
			onblur: () => {
				if (!this._uid) return;
				this.events.fire(ItemEvent.blur, [this.getValue()]);
				this.paint();
			},
			onkeydown: (event: KeyboardEvent) => {
				if (event.code === "Enter" && this.config.type === "input") event.preventDefault();
				this.events.fire(ItemEvent.keydown, [event]);
			},
		};
	}
	protected _draw() {
		const {
			id,
			value,
			disabled,
			name,
			icon,
			placeholder,
			required,
			inputType,
			hidden,
			autocomplete,
			readOnly,
			maxlength,
			minlength,
			max,
			min,
			label,
			helpMessage,
		} = this.config;

		const visibility = hidden ? " dhx_form-group--hidden" : "";
		const activeFocus = this.getRootView()?.refs?.input?.el === document.activeElement;

		return el(
			"div.dhx_form-group",
			{
				class: getFormItemCss(this.config, isVerify(this.config), activeFocus) + visibility,
			},
			[
				this._drawLabel(),
				el(".dhx_input__wrapper", {}, [
					el("div.dhx_input__container", {}, [
						this.config.icon
							? el(".dhx_input__icon", {
									class: this.config.icon,
							  })
							: null,
						el("input.dhx_input", {
							type: ["text", "number", "password"].includes(inputType) ? inputType : "text",
							"data-dhx-id": name || id,
							id: id || this._uid,
							placeholder: placeholder || "",
							value: isDefined(value) ? value : "",
							name: name || "",
							disabled,
							required,
							readOnly,
							maxlength,
							minlength,
							max,
							min,
							onblur: this._handlers.onblur,
							oninput: this._handlers.oninput,
							onchange: this._handlers.onchange,
							onfocus: this._handlers.onfocus,
							onkeydown: this._handlers.onkeydown,
							class: icon ? "dhx_input--icon-padding" : "",
							autocomplete: autocomplete ? "on" : "off",
							_ref: "input",
							"aria-label": label || helpMessage || `type ${name || inputType || "text"}`,
							"aria-describedby": helpMessage ? `dhx_label__help_${id || this._uid}` : null,
						}),
					]),
					getValidationMessage(this.config) &&
						el("span.dhx_input__caption", getValidationMessage(this.config)),
				]),
			]
		);
	}
}
